import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges } from '@angular/core';
import {
  type SubThemeSentimentBreakdown,
  isSubThemeDistributionRowSuggested,
} from '../../../../services/sentiment-analysis-stats/sentiment-analysis-stats.service';

export interface SubThemeBarRow {
  subThemeId: string;
  subThemeName: string;
  positive: number;
  neutral: number;
  negative: number;
  positiveCount: number;
  neutralCount: number;
  negativeCount: number;
}

@Component({
  selector: 'app-sentiment-details-subtheme-breakup',
  standalone: false,
  templateUrl: './details-subtheme-breakup.component.html',
  styleUrls: ['./details-subtheme-breakup.component.scss'],
})
export class SentimentDetailsSubthemeBreakupComponent implements OnChanges {
  @Input() subThemeBreakdown: SubThemeSentimentBreakdown[] = [];
  /** Set by parent when user clicks a theme bar in Theme break-up (no dropdown in this card). */
  @Input() selectedThemeId: string = '';
  /** When set, the matching sub-theme row is shown as selected (synced from Trend card selection). */
  @Input() selectedSubThemeId: string | null = null;
  /** Emitted when a sub-theme row/bar is clicked – parent updates Trend/Cohort card. */
  @Output() subThemeClick = new EventEmitter<{ themeId: string; subThemeId: string }>();
  /** Emitted when the info icon is clicked – parent opens sub-theme side panel. */
  @Output() subThemeInfoClick = new EventEmitter<SubThemeBarRow>();
  /** Emitted when the Suggested toggle hides the currently selected sub-theme – parent should clear trend sub-theme. */
  @Output() selectedSubThemeHiddenByFilter = new EventEmitter<void>();

  /** When true, list only suggested sub-theme rows (sub-theme and/or parent theme suggested). */
  showSuggestedSubthemesOnly = false;

  /**
   * Cached rows for `*ngFor` — same pattern as Theme break-up: avoids a new array every CD (scroll reset).
   */
  subThemeBarRows: SubThemeBarRow[] = [];

  /** At least one suggested sub-theme exists for the theme currently selected in Theme break-up. */
  get hasSuggestedSubthemesForSelectedTheme(): boolean {
    if (!this.selectedThemeId) return false;
    return (this.subThemeBreakdown || []).some(
      (s) => s.themeId === this.selectedThemeId && isSubThemeDistributionRowSuggested(s),
    );
  }

  /** No non-suggested sub-themes for this theme (default list would be empty) — switch to suggested view. */
  private shouldAutoSuggestSubthemeMode(): boolean {
    if (!this.selectedThemeId) return false;
    const forTheme = (this.subThemeBreakdown || []).filter((s) => s.themeId === this.selectedThemeId);
    if (!forTheme.length) return false;
    const hasNonSuggested = forTheme.some((s) => !isSubThemeDistributionRowSuggested(s));
    if (hasNonSuggested) return false;
    return forTheme.some((s) => isSubThemeDistributionRowSuggested(s));
  }

  ngOnChanges(changes: SimpleChanges): void {
    const sel = changes['selectedThemeId'];
    if (sel && sel.previousValue !== sel.currentValue) {
      this.showSuggestedSubthemesOnly = false;
    }

    if (!this.hasSuggestedSubthemesForSelectedTheme && this.showSuggestedSubthemesOnly) {
      this.showSuggestedSubthemesOnly = false;
    }

    if (this.shouldAutoSuggestSubthemeMode()) {
      this.showSuggestedSubthemesOnly = true;
    }

    this.rebuildSubThemeBarRows();
  }

  onSuggestedSubthemesToggle(): void {
    this.showSuggestedSubthemesOnly = !this.showSuggestedSubthemesOnly;
    this.rebuildSubThemeBarRows();
    const ids = new Set(this.subThemeBarRows.map((r) => r.subThemeId));
    if (this.selectedSubThemeId && !ids.has(this.selectedSubThemeId)) {
      this.selectedSubThemeHiddenByFilter.emit();
    }
  }

  private rebuildSubThemeBarRows(): void {
    if (!this.selectedThemeId) {
      this.subThemeBarRows = [];
      return;
    }
    const forTheme = (this.subThemeBreakdown || []).filter((s) => s.themeId === this.selectedThemeId);
    const filtered = forTheme.filter((s) =>
      this.showSuggestedSubthemesOnly
        ? isSubThemeDistributionRowSuggested(s)
        : !isSubThemeDistributionRowSuggested(s),
    );
    this.subThemeBarRows = filtered.map((s) => {
      const p = s.sentiment?.positive?.count ?? 0;
      const n = s.sentiment?.neutral?.count ?? 0;
      const neg = s.sentiment?.negative?.count ?? 0;
      const total = p + n + neg || 1;
      return {
        subThemeId: s.subThemeId,
        subThemeName: s.subThemeName,
        positive: (p / total) * 100,
        neutral: (n / total) * 100,
        negative: (neg / total) * 100,
        positiveCount: p,
        neutralCount: n,
        negativeCount: neg,
      };
    });
  }

  trackBySubThemeId(_index: number, row: SubThemeBarRow): string {
    return row.subThemeId;
  }

  onSubThemeClick(row: SubThemeBarRow): void {
    if (!this.selectedThemeId) return;
    this.subThemeClick.emit({ themeId: this.selectedThemeId, subThemeId: row.subThemeId });
  }

  onSubThemeInfoClick(row: SubThemeBarRow, event: Event): void {
    event.stopPropagation();
    this.subThemeInfoClick.emit(row);
  }
}
